import { memo, useCallback } from 'react';
import type {
  FaceState,
  OdontogramTool,
  ToothChart,
  ToothSurface,
} from '../odontogram.types';

const FACE_CLASSES: Record<FaceState, string> = {
  healthy: 'fill-white',
  pending: 'fill-red-500',
  existing: 'fill-blue-600',
};

const SURFACE_LABELS: Record<ToothSurface, string> = {
  vestibular: 'vestibular o labial',
  lingual: 'lingual o palatina',
  mesial: 'mesial',
  distal: 'distal',
  occlusal: 'oclusal o incisal',
};

interface SurfaceDefinition {
  surface: ToothSurface;
  points: string;
}

const baseSurfaces = (
  upper: boolean,
  rightQuadrant: boolean,
): SurfaceDefinition[] => {
  const outerLeft = 6;
  const outerRight = 50;
  const innerLeft = 16;
  const innerRight = 40;
  const outerTop = upper ? 20 : 6;
  const outerBottom = outerTop + 36;
  const innerTop = outerTop + 10;
  const innerBottom = outerBottom - 10;
  const top: ToothSurface = upper ? 'vestibular' : 'lingual';
  const bottom: ToothSurface = upper ? 'lingual' : 'vestibular';
  const left: ToothSurface = rightQuadrant ? 'distal' : 'mesial';
  const right: ToothSurface = rightQuadrant ? 'mesial' : 'distal';

  return [
    {
      surface: top,
      points: `${outerLeft},${outerTop} ${outerRight},${outerTop} ${innerRight},${innerTop} ${innerLeft},${innerTop}`,
    },
    {
      surface: right,
      points: `${outerRight},${outerTop} ${outerRight},${outerBottom} ${innerRight},${innerBottom} ${innerRight},${innerTop}`,
    },
    {
      surface: bottom,
      points: `${outerRight},${outerBottom} ${outerLeft},${outerBottom} ${innerLeft},${innerBottom} ${innerRight},${innerBottom}`,
    },
    {
      surface: left,
      points: `${outerLeft},${outerBottom} ${outerLeft},${outerTop} ${innerLeft},${innerTop} ${innerLeft},${innerBottom}`,
    },
    {
      surface: 'occlusal',
      points: `${innerLeft},${innerTop} ${innerRight},${innerTop} ${innerRight},${innerBottom} ${innerLeft},${innerBottom}`,
    },
  ];
};

interface ToothProps {
  tooth: ToothChart;
  selectedTool: OdontogramTool;
  labelBelow: boolean;
  onApply: (toothId: number, surface: ToothSurface) => void;
  onSelect: (toothId: number) => void;
}

function ToothComponent({
  tooth,
  selectedTool,
  labelBelow,
  onApply,
  onSelect,
}: ToothProps) {
  const quadrant = Math.floor(tooth.toothId / 10);
  const upper = quadrant === 1 || quadrant === 2 || quadrant === 5 || quadrant === 6;
  const rightQuadrant = quadrant === 1 || quadrant === 4 || quadrant === 5 || quadrant === 8;
  const surfaces = baseSurfaces(upper, rightQuadrant);
  const canResetAbsent = tooth.absent && selectedTool === 'clearTooth';
  const toothOutline = upper
    ? 'M13 2 H43 L50 20 V56 H6 V20 Z'
    : 'M6 6 H50 V42 L43 62 H13 L6 42 Z';
  const crownClass = tooth.crown === 1
    ? 'stroke-red-600'
    : tooth.crown === 2
      ? 'stroke-slate-400'
      : '';

  const handleSurface = useCallback((surface: ToothSurface) => {
    onSelect(tooth.toothId);
    if (!tooth.absent) onApply(tooth.toothId, surface);
  }, [onApply, onSelect, tooth.absent, tooth.toothId]);

  const toothGraphic = (
    <div className="relative h-16 w-14 shrink-0 rounded-md transition hover:bg-cyan-50/60">
      <svg
        viewBox="0 0 56 64"
        className="h-16 w-14 overflow-visible"
        aria-label={`Diente ${tooth.toothId}`}
      >
        <path
          d={toothOutline}
          className="fill-slate-50 stroke-slate-400 stroke-[1.25]"
          aria-hidden="true"
        />
        {surfaces.map(({ surface, points }) => (
          <polygon
            key={surface}
            points={points}
            role="button"
            tabIndex={tooth.absent ? -1 : 0}
            aria-disabled={tooth.absent}
            aria-label={`Diente ${tooth.toothId}, cara ${SURFACE_LABELS[surface]}: ${tooth.faces[surface]}`}
            className={[
              FACE_CLASSES[tooth.faces[surface]],
              'cursor-pointer stroke-slate-500 stroke-[1.15] transition hover:brightness-95',
              'focus:outline-none focus:stroke-cyan-700 focus:stroke-[2.5]',
              tooth.absent ? 'pointer-events-none opacity-50' : '',
            ].join(' ')}
            onClick={() => handleSurface(surface)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSurface(surface);
              }
            }}
          >
            <title>{`Diente ${tooth.toothId} · ${SURFACE_LABELS[surface]} · ${tooth.faces[surface]}`}</title>
          </polygon>
        ))}
        {crownClass ? (
          <path
            d={toothOutline}
            className={`pointer-events-none fill-none stroke-[3] ${crownClass}`}
            aria-hidden="true"
          />
        ) : null}
        {tooth.endodontics ? (
          <g className="pointer-events-none">
            <line
              x1="23"
              y1={upper ? 30 : 20}
              x2="23"
              y2={upper ? 58 : 58}
              className={`${tooth.endodontics === 1 ? 'stroke-red-600' : 'stroke-blue-700'} stroke-[2.5]`}
            />
            <line
              x1="33"
              y1={upper ? 30 : 20}
              x2="33"
              y2={upper ? 58 : 58}
              className={`${tooth.endodontics === 1 ? 'stroke-red-600' : 'stroke-blue-700'} stroke-[2.5]`}
            />
          </g>
        ) : null}
        {tooth.bridge ? (
          <line
            x1="0"
            y1={upper ? 10 : 54}
            x2="56"
            y2={upper ? 10 : 54}
            className={`${tooth.bridge === 1 ? 'stroke-red-600' : 'stroke-blue-700'} pointer-events-none stroke-[3]`}
          />
        ) : null}
        {tooth.absent ? (
          <g className="pointer-events-none">
            <line x1="8" y1="4" x2="48" y2="60" className="stroke-red-700 stroke-[4]" />
            <line x1="48" y1="4" x2="8" y2="60" className="stroke-red-700 stroke-[4]" />
          </g>
        ) : null}
      </svg>

      {tooth.absent ? (
        <button
          type="button"
          disabled={!canResetAbsent}
          aria-label={
            canResetAbsent
              ? `Restablecer diente ${tooth.toothId}`
              : `Diente ${tooth.toothId} ausente; seleccione Restablecer pieza para modificarlo`
          }
          title={
            canResetAbsent
              ? 'Restablecer pieza'
              : 'Seleccione “Restablecer pieza” para volver a habilitarla'
          }
          onClick={() => onApply(tooth.toothId, 'occlusal')}
          className="absolute inset-0 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-600 disabled:cursor-not-allowed"
        />
      ) : null}
    </div>
  );

  return (
    <div className="flex w-14 shrink-0 flex-col items-center gap-1">
      {!labelBelow ? (
        <button type="button" onClick={() => onSelect(tooth.toothId)}
          className="text-[11px] font-bold tabular-nums text-slate-600 hover:text-cyan-700">
          {tooth.toothId}
        </button>
      ) : null}
      {toothGraphic}
      {labelBelow ? (
        <button type="button" onClick={() => onSelect(tooth.toothId)}
          className="text-[11px] font-bold tabular-nums text-slate-600 hover:text-cyan-700">
          {tooth.toothId}
        </button>
      ) : null}
    </div>
  );
}

export const Tooth = memo(ToothComponent);
